import cx from "classnames";
import React from "react";

import styles from "../../styles/InlineSelect.module.less";

type InlineSelectValue = string | number | null;

type OptionProps<T> = { children: React.ReactNode; value: T };

// Option exposed to the user for config (<InlineSelect.Option />)
const Option = <T extends InlineSelectValue>({ children, value }: OptionProps<T>) => {
  return <React.Fragment key={value}>{children}</React.Fragment>;
};

type InternalOptionProps<T> = {
  children: React.ReactNode;
  className?: string;
  activeClassName?: string;
  value: T;
  onClick: (value: T) => void;
  active: boolean;
  disabled: boolean;
};

// Internal Option to render and control appearance
const InternalOption = <T extends InlineSelectValue>({
  children,
  className,
  activeClassName,
  value,
  onClick,
  active,
  disabled,
}: InternalOptionProps<T>) => {
  return (
    <button
      onClick={() => onClick(value)}
      type="button"
      disabled={disabled}
      aria-pressed={active}
      className={cx(className ?? styles.option, active && (activeClassName ?? styles.active))}
    >
      {children}
    </button>
  );
};

type InlineSelectProps<T> = {
  children: React.ReactElement<OptionProps<T>>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  styles?: {
    input?: string;
    option?: string;
    active?: string;
  };
};

const InlineSelect = <T extends InlineSelectValue>({
  children,
  value,
  onChange,
  disabled = false,
  styles: propStyles = {},
}: InlineSelectProps<T>) => {
  return (
    <div className={propStyles.input ?? styles.input}>
      {React.Children.map(children, (child) => {
        if (!child || !child.props) {
          // Gracefully ignore false-y values by not showing anything
          return null;
        }

        const { value: childValue } = child.props;

        return (
          <InternalOption<T>
            value={childValue}
            key={childValue}
            active={value === childValue}
            disabled={disabled}
            onClick={onChange}
            className={propStyles.option}
            activeClassName={propStyles.active}
          >
            {child}
          </InternalOption>
        );
      })}
    </div>
  );
};

export default Object.assign(InlineSelect, { Option });
